import p5 from "p5";
(window as any).p5 = p5;
import { globals, UnitRegistry } from "./types";

import { Starter, Tilemap, Graphics, Assets, MigScreen, Keys, Rect, Vec2, MigButton, Color, } from "miglib";
import { GameObj } from "./GameObj";
import { World } from "./World";
import { Tile } from "./Tile";
import { ObjGen } from "./ObjGen";
import { RenderUtil } from "./RenderUtil";
import { InfluenceMap } from "./InfluenceMap";
import { Faction } from "./types";
import { positionsInRadius, randomPositionInRadius } from "./geometry";
import { mulberry32 } from "./rng";
import { SaveManager } from "./SaveManager";
import { Stats } from "./Stats";

const TUTORIAL_SEED = 893927680;
import { UnitType, unitRegistry } from "./units";
import type { UnitContext, PlaceContext, ObjContext, RenderContext, MakeContext, DefeatContext } from "./units";
import { setCurrentScreen } from "./main";
import { MenuScreen } from "./MenuScreen";

interface FactionData {
    blood: number
    souls: number
    runes: number
}

// TODO
function initialData1(faction: Faction, difficulty: number): FactionData { return { blood: 2000, souls: 2000, runes: 2000 } }
function initialData(faction: Faction, difficulty: number): FactionData {
    return {
        blood: faction === "player" ? 300 : 200 + difficulty * 100,
        souls: faction === "player" ? 5 : 5 + difficulty * 3,
        runes: faction === "player" ? 2 : 2 + difficulty * 2,
    }
}
function initialAITracking() { return { objs: new Array<GameObj>() } }

export class GameScreen implements MigScreen {




    // #region fields

    private currentTutorialStep = 0;
    private readonly neutralUnitMaxAmount;

    readonly factionData;
    get playerData() { return this.factionData.player }
    private readonly AITracking = { "player": initialAITracking(), "green": initialAITracking(), "blue": initialAITracking(), "neutral": initialAITracking() }

    // map?: Tilemap;
    private camX = 0;
    private camY = 0;
    private zoom = 4;

    private dragging = false;
    private lastMouseX = 0;
    private lastMouseY = 0;
    private hovered: GameObj | UnitType | null = null;

    readonly overworld: World;
    readonly netherworld: World;
    readonly influenceMap: InfluenceMap;
    private dragObj: GameObj | UnitType | null = null;

    private consumedPressed = false;
    private wasPressed = false;
    private justPressed = false;
    private justReleased = false;

    private isOverworld = true;
    private nextFactionStepTime = 0;

    private difficulty;

    private loaded = false;
    private nextSaveTime = 0;

    public ended = false;
    private endIsWin = false;
    private selectedEndTab = 0;
    private endScreenHidden = false;

    readonly stats: Stats;

    readonly units: UnitRegistry = unitRegistry;

    // #endregion





    // #region Setup

    constructor(difficultyIn: number, worldSizeIn: number, isTutorialOn: boolean, shouldLoad: boolean) {

        const data = shouldLoad && SaveManager.hasSave() ? SaveManager.read() : null;

        if (data) {
            this.neutralUnitMaxAmount = data.neutralUnitMaxAmount;
            this.factionData = data.factionData;
            this.difficulty = data.difficulty;
            this.currentTutorialStep = data.currentTutorialStep;
            this.camX = data.camX;
            this.camY = data.camY;
            this.zoom = data.zoom;
            this.isOverworld = data.isOverworld;

            const idMap = new Map<string, GameObj>();
            this.overworld = SaveManager.buildWorld(data.overworld, idMap);
            this.netherworld = SaveManager.buildWorld(data.netherworld, idMap);
            this.influenceMap = new InfluenceMap(this.overworld, this.netherworld);
            SaveManager.applyInfluence(this.influenceMap, data.influence);
            SaveManager.syncIdCounter(idMap);

            const resolve = (ids: string[]) => ids.map(id => idMap.get(id)).filter((o): o is GameObj => !!o);
            this.AITracking.player.objs = resolve(data.aiTracking.player);
            this.AITracking.green.objs = resolve(data.aiTracking.green);
            this.AITracking.blue.objs = resolve(data.aiTracking.blue);
            this.AITracking.neutral.objs = resolve(data.aiTracking.neutral);

            this.stats = Stats.fromData(data.stats);

            this.loaded = true;
            return;
        }

        this.stats = new Stats(Date.now());

        this.neutralUnitMaxAmount = 20 + 10 * worldSizeIn;
        this.factionData = {
            "player": initialData("player", difficultyIn),
            "green": initialData("green", difficultyIn),
            "blue": initialData("blue", difficultyIn),
            "neutral": initialData("neutral", difficultyIn)
        }

        if (!isTutorialOn) this.currentTutorialStep = -1;

        const seed = isTutorialOn ? TUTORIAL_SEED : (Math.random() * 0x100000000) >>> 0;
        const rng = mulberry32(seed);

        this.difficulty = difficultyIn;
        const worldSize = 40 + 12 * worldSizeIn;

        this.overworld = new World(worldSize, true, {
            makeOcean: () => new Tile("ocean", "ocean"),
            makeLand: (variant: string) => new Tile("land_" + variant, "land"),
            makeTree: () => new Tile(rng() < .5 ? "tree1" : "tree2", "tree"),
        }, rng());
        this.netherworld = new World(worldSize, false, {
            makeOcean: () => new Tile("lava", "ocean"),
            makeLand: (variant: string) => new Tile("nether_" + variant, "land"),
            makeTree: () => new Tile(rng() < .5 ? "rock1" : "rock2", "tree"),
        }, rng());
        this.influenceMap = new InfluenceMap(this.overworld, this.netherworld);

        new ObjGen(
            this,
            this.units,
            worldSize,
            this.overworld,
            this.netherworld,
            rng,
            isTutorialOn
        ).setup();

    }

    save(): void {
        SaveManager.write({
            version: SaveManager.VERSION,
            neutralUnitMaxAmount: this.neutralUnitMaxAmount,
            difficulty: this.difficulty,
            currentTutorialStep: this.currentTutorialStep,
            camX: this.camX,
            camY: this.camY,
            zoom: this.zoom,
            isOverworld: this.isOverworld,
            factionData: this.factionData,
            overworld: SaveManager.serializeWorld(this.overworld),
            netherworld: SaveManager.serializeWorld(this.netherworld),
            influence: SaveManager.serializeInfluence(this.influenceMap),
            aiTracking: {
                player: this.AITracking.player.objs.map(o => o.id),
                green: this.AITracking.green.objs.map(o => o.id),
                blue: this.AITracking.blue.objs.map(o => o.id),
                neutral: this.AITracking.neutral.objs.map(o => o.id),
            },
            stats: this.stats.toData(),
        });
    }

    private autosave(): void {
        const now = Date.now();
        if (now < this.nextSaveTime) return;
        this.nextSaveTime = now + 30000;
        this.save();
    }

    onShow(g: Graphics): void {

        if (!this.loaded) {
            this.camX = 611;
            this.camY = 696;
            this.swapWorld();
            const seat = this.netherworld.find(x => x.unitId === "seat") as GameObj;
            const { x, y } = this.netherworld.getPos(seat.id) as Vec2;
            this.centerCameraOn(g, x, y)
        }

        g.sketch.mouseWheel = (e: WheelEvent) => this.onMouseWheel(e.deltaY > 0)
    }

    // #endregion





    // #region World Access

    swapWorld() {
        if (this.dragObj) return;
        globals.audio.playSound("swoosh");
        this.isOverworld = !this.isOverworld;
    }

    get currentWorld() {
        return this.isOverworld ? this.overworld : this.netherworld;
    }

    get otherWorld() {
        return this.isOverworld ? this.netherworld : this.overworld;
    }

    // #endregion





    // #region paying costs

    canPayUnitCost(faction: Faction, unit: UnitType) {
        const cost = unit.cost();
        return this.canPayCost(faction, cost.blood, cost.souls, cost.runes)
    }

    payUnitCost(faction: Faction, unit: UnitType) {
        const cost = unit.cost();
        this.factionData[faction].blood -= cost.blood;
        this.factionData[faction].souls -= cost.souls;
        this.factionData[faction].runes -= cost.runes;
    }

    canPayCost(faction: Faction, blood: number, souls: number, runes: number) {
        return this.factionData[faction].blood >= blood
            && this.factionData[faction].souls >= souls
            && this.factionData[faction].runes >= runes
    }

    addResource(faction: Faction, kind: "blood" | "souls" | "runes", amount: number) {
        this.factionData[faction][kind] += amount;
        if (faction === "player" && amount > 0) this.stats.recordResource(kind, amount);
    }
    // #endregion





    // #region world utils

    tryMove(obj: GameObj, x: number, y: number, world: World, isNewPiece: boolean = false): boolean {

        const finallly = () => {
            if (isNewPiece) {
                world.set(x, y, obj)
            } else {
                world.move(obj.id, x, y)
            }
        }

        const tile = world.getTile(x, y);
        if (!tile) return false;

        const unit = this.units[obj.unitId];

        if (!unit.canEnter({ game: this, tile, unit, world, x, y, faction: obj.faction })) {
            return false;
        }

        const target = world.get(x, y);

        if (target) {
            if (target.faction === obj.faction) return false;

            const defenderUnit = this.units[target.unitId];

            if (!unit.canDefeat(defenderUnit.id)) {
                return false;
            } else {

                globals.audio.playSound("dmg");
                defenderUnit.onDefeat({ game: this, attacker: obj, defender: target })
                if (obj.faction === "player") this.stats.recordEnemyDefeated(target.unitId);
                world.remove(x, y);

                finallly();
                return true;
            }

        } else {
            finallly();
            return true;
        }

    }

    findRandomPosForObj(range: number, ctx: ObjContext, canBeIn: (ctx: PlaceContext) => boolean, towards?: Vec2): { x: number, y: number, tile: Tile } | null {
        const { x, y } = randomPositionInRadius(ctx.x, ctx.y, range, towards);
        const tile = ctx.world.getTile(x, y);
        if (!tile) return null;
        if (!canBeIn({ ...ctx, x, y, tile })) return null;
        return { x, y, tile };
    }

    // #endregion





    // #region Render

    onRender(g: Graphics): void {
        const s = g.sketch;
        let mxy: Vec2 = { x: g.mx / this.zoom, y: g.my / this.zoom };
        this.updateMouse(g);
        s.background(30);

        this.checkKeyboard(g);

        s.push();
        s.scale(this.zoom);

        this.hovered = null;

        const viewW = g.width / this.zoom;
        const viewH = g.height / this.zoom;
        const pad = 16;
        const minX = Math.floor((this.camX - pad) / 16);
        const maxX = Math.ceil((this.camX + viewW + pad) / 16);
        const minY = Math.floor((this.camY - pad) / 16);
        const maxY = Math.ceil((this.camY + viewH + pad) / 16);

        const isVisible = (x: number, y: number) =>
            x >= minX && x <= maxX && y >= minY && y <= maxY;

        this.renderTiles(g, mxy, isVisible);
        this.renderObjs(g, mxy, isVisible);
        this.renderDragObj(g, mxy);

        s.pop();

        mxy = { x: g.mx / 4, y: g.my / 4 };

        s.push();
        s.scale(4);
        // UI
        this.renderTopUI(g, mxy);
        this.renderBotUI(g, mxy);
        this.renderSwapButton(g, mxy);
        if (this.hovered !== null) this.renderHoveredUnitUI(g, this.hovered);
        if (this.isInTutorial()) this.renderTutorial(g, mxy, this.currentTutorialStep);

        this.checkGameEnd();
        this.renderEnd(g, mxy, this.endIsWin);

        s.pop();


        this.updateDrag(g);

        this.stepAIFactions();

        this.autosave();

    }

    private countSeats(faction: Faction): number {
        let n = 0;
        for (const { obj } of this.overworld) if (obj.unitId === "seat" && obj.faction === faction) n++;
        for (const { obj } of this.netherworld) if (obj.unitId === "seat" && obj.faction === faction) n++;
        return n;
    }

    private checkGameEnd(): void {
        if (this.ended) return;

        const playerSeats = this.countSeats("player");
        const enemySeats = this.countSeats("green") + this.countSeats("blue");

        if (playerSeats === 0) {
            this.ended = true;
            this.endIsWin = false;
        } else if (enemySeats === 0) {
            this.ended = true;
            this.endIsWin = true;
        }

        if (this.ended) {
            this.stats.markEnded(Date.now());
            this.save();
        }
    }

    renderEnd(g: Graphics, mxy: Vec2, isWin: boolean): void {

        if (!this.ended || this.endScreenHidden) return; // TODO

        const w = 256;
        const h = 9 * 16;
        const cx = Math.floor(g.width / 4 / 2);
        const cy = Math.floor(g.height / 4 / 2);
        const bounds = { x: cx - w / 2, y: cy - h / 2, w, h }

        const d = 166;
        const tabs = [
            {
                name: "summary",
                x: cx - d + 49,
            },
            {
                name: "created",
                x: cx - d + 109,
            },
            {
                name: "destroyed",
                x: cx - d + 169,
            },
            {
                name: "resources",
                x: cx - d + 229,
            },
        ];

        g.renderText("", 0, 0, { size: 6, color: Color.col(255, 255, 255) });

        let i = 0;
        for (const { name, x } of tabs) {
            const isSelected = this.selectedEndTab === i;
            const w = 56;
            const tabBounds = { x, y: bounds.y - 13, w, h: 24 }
            const isHovered = Rect.isIn(mxy, tabBounds);
            if (isHovered || isSelected) tabBounds.y -= 2
            if (isHovered && !this.consumedPressed && this.justPressed) {
                this.consumedPressed = true;
                this.selectedEndTab = i;
            }
            RenderUtil.render9Slice(g, tabBounds.x, tabBounds.y, tabBounds.w, tabBounds.h);
            const lineW = g.textWidth(name);
            g.renderText(name, x + tabBounds.w / 2 - lineW / 2, tabBounds.y + 11);
            i++;
        }

        RenderUtil.render9Slice(g, bounds.x, bounds.y, bounds.w, bounds.h);

        const renderLines = (dy: number, lines: string[]) => {
            for (const line of lines) {
                const lineW = g.textWidth(line);
                g.renderText(line, cx - lineW / 2, bounds.y + dy);
                dy += 13;
            }
        }

        if (this.selectedEndTab === 0) {
            g.renderText("", 0, 0, { size: 10, color: Color.col(255, 255, 255) });

            let dy = 30;

            const title = isWin ? "VICTORY" : "DEFEAT";
            const titleW = g.textWidth(title);
            g.renderText(title, cx - titleW / 2, bounds.y + dy)

            g.renderText("", 0, 0, { size: 5, color: Color.col(255, 255, 255) });
            dy += 20;

            const lines = [
                "elapsed: " + this.stats.elapsedFormatted(),
                "units created:" + this.stats.total(this.stats.unitsCreated),
                "enemies defeated: " + this.stats.total(this.stats.enemiesDefeated),
                "resources generated: " + this.stats.total(this.stats.resourcesGenerated),
                "score: " + this.stats.score(),
            ];

            renderLines(dy, lines);

        }


        g.renderText("", 0, 0, { size: 5, color: Color.col(255, 255, 255) });

        if (this.selectedEndTab === 1) {
            const lines = [
                "UNITS SPAWNED",
                ...Object.keys(this.stats.unitsCreated).map(x => x + "s: " + (this.stats.unitsCreated[x] ?? 0)),
                "priests converted: " + this.stats.unitsConverted
            ]
            renderLines(20, lines)
        }

        if (this.selectedEndTab === 2) {
            const lines = [
                "ENEMIES DEFEATED",
                "",
                ...Object.keys(this.stats.enemiesDefeated).map(x => x + "s: " + (this.stats.enemiesDefeated[x] ?? 0)),
                "fire rain kills: " + this.stats.fireRainDestroyed
            ]
            renderLines(20, lines)
        }

        if (this.selectedEndTab === 3) {
            const lines = [
                "RESOURCES GENERATED",
                "",
                "souls: " + this.stats.resourcesGenerated.souls,
                "blood: " + this.stats.resourcesGenerated.blood,
                "runes: " + this.stats.resourcesGenerated.runes,
            ]
            renderLines(20, lines)
        }

        [{
            dx: 16,
            text: "continue",
            callback: () => { this.endScreenHidden = true },
        }, {
            dx: -16 - 64,
            text: "exit",
            callback: () => { this.save(); setCurrentScreen(new MenuScreen()); },
        }].forEach(({ dx, text, callback }) => {
            const bounds = { x: cx + dx, y: cy + 64, w: 64, h: 16 }
            const isHovered = Rect.isIn(mxy, bounds);
            if (isHovered) bounds.y -= 1;
            RenderUtil.render9Slice(g, bounds.x, bounds.y, bounds.w, bounds.h);
            const textW = g.textWidth(text);
            g.renderText(text, bounds.x + 32 - textW / 2, bounds.y + 10);
            if (isHovered && this.justPressed && !this.consumedPressed) {
                this.consumedPressed = true;
                globals.audio.playSound("tick")
                callback();
            }
        })
    }

    renderTiles(g: Graphics, mxy: Vec2, isVisible: (x: number, y: number) => boolean) {
        for (let { x, y, tile } of this.currentWorld.tilesIterator()) {
            if (!isVisible(x, y)) continue;
            this.render(g, tile.img, x, y);
            const influence = this.influenceMap.get(x, y);
            if (influence) {
                this.render(g, "influence/" + influence, x, y);
                this.renderInfluenceBorders(g, x, y, influence);
            }
            if (this.dragObj !== null && !(this.dragObj instanceof GameObj)) {
                const unit = this.dragObj as UnitType;
                const ctx: PlaceContext = { game: this, unit, tile, world: this.currentWorld, x, y, faction: "player" };
                const objAlreadyThere = this.currentWorld.get(x, y);
                if (
                    tile
                    && this.influenceMap.get(ctx.x, ctx.y) === "player"
                    && unit.canEnter(ctx)
                    && (!objAlreadyThere || unit.canDefeat(objAlreadyThere.unitId))
                ) {
                    this.render(g, "square", x, y)
                }
            }
        }
    }

    static shuffle<T>(arr: T[]): T[] {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }

    renderObjs(g: Graphics, mxy: Vec2, isVisible: (x: number, y: number) => boolean) {

        const unitsToStep: ObjContext[] = [];

        for (let { x, y, obj } of this.otherWorld) {
            const unit = this.units[obj.unitId];
            const ctx: ObjContext = { game: this, unit, obj, world: this.otherWorld, x, y, faction: obj.faction };

            if (obj.faction === "player" || unit.aiAlwaysSteps()) unit.step(ctx);
            else unitsToStep.push(ctx)

            const influenceRange = unit.influenceRange();
            if (influenceRange > 0) unit.spreadInfluence(ctx, influenceRange, .2);

            if (obj.faction !== "player") obj.nudgeForAI(this.difficulty);

            if (unit.id === "pentagram") {
                g.sketch.tint(255, 128);
                const bounds: Rect = { x: -this.camX + x * 16, y: -this.camY + y * 16, w: 16, h: 16 };
                unit.render({ ...ctx, g, isHovered: false, bounds })
                g.sketch.tint(255, 255);
            }
        }

        for (let { x, y, obj } of this.currentWorld) {
            const unit = this.units[obj.unitId];
            const ctx: ObjContext = { game: this, unit, obj, world: this.currentWorld, x, y, faction: obj.faction };

            if (obj.faction === "player" || unit.aiAlwaysSteps()) unit.step(ctx);
            else unitsToStep.push(ctx)

            const influenceRange = unit.influenceRange();
            if (influenceRange > 0) unit.spreadInfluence(ctx, influenceRange, .2);

            if (obj.faction !== "player") obj.nudgeForAI(this.difficulty);

            if (!isVisible(x, y)) continue;
            const bounds: Rect = { x: -this.camX + x * 16, y: -this.camY + y * 16, w: 16, h: 16 };
            let isHovered: boolean = Rect.isIn(mxy, bounds)
            unit.render({ ...ctx, g, isHovered, bounds })
        }

        const willStep = GameScreen.shuffle(unitsToStep).filter(x => x.obj.isReady(x.unit.maxCooldown())).splice(0, 1 + this.difficulty);
        for (const ctx of willStep) {
            ctx.unit.step(ctx)
        }
    }

    renderDragObj(g: Graphics, mxy: Vec2) {
        if (this.dragObj === null) return;

        if (this.dragObj instanceof GameObj) {

            const obj = this.dragObj as GameObj;
            const unit = this.units[obj.unitId];
            const { x, y } = this.currentWorld.getPos(obj.id) as Vec2;
            const ctx: UnitContext = { game: this, unit, world: this.currentWorld, faction: "player" }

            const canEnter = (x: number, y: number) => {
                const tile = this.currentWorld.getTile(x, y);
                if (!tile) return false;
                return unit.canEnter({ game: this, tile, unit, world: this.currentWorld, x, y, faction: "player" }); // LOOK HERE
            }

            const reach = unit.getReachablePositions(x, y, (x, y) => {
                const objectAlreadyThere = ctx.world.get(x, y);
                return canEnter(x, y) && (!objectAlreadyThere || unit.canDefeat(objectAlreadyThere.unitId))
            });
            for (let reachPos of reach) {
                this.render(g, "square", reachPos.x, reachPos.y);
            }

            const renderX = mxy.x - 8, renderY = mxy.y - 8;
            g.renderImage(unit.img(ctx), renderX, renderY);

            if (this.justReleased) {
                let didMove = false;
                const newX = Math.round((mxy.x - 8 + this.camX) / 16);
                const newY = Math.round((mxy.y - 8 + this.camY) / 16);
                const isReachable = reach.some(p => p.x === newX && p.y === newY);
                if (isReachable && (newX !== x || newY !== y) && canEnter(newX, newY)) {
                    const moved = this.tryMove(obj, newX, newY, this.currentWorld);
                    didMove = true;
                    globals.audio.playSound("move");
                    if (moved) obj.setNotReady();
                }
                if (!didMove)
                    globals.audio.playSound("nope");

                this.dragObj = null;
            }

        } else {

            const unit = this.dragObj as UnitType;
            const x = mxy.x - 8, y = mxy.y - 8;
            const ctx: UnitContext = { game: this, unit, world: this.otherWorld, faction: "player" }

            g.renderImage(unit.img(ctx), x, y);

            if (this.justReleased) {
                let deployed = false;
                const newX = Math.round((mxy.x - 8 + this.camX) / 16);
                const newY = Math.round((mxy.y - 8 + this.camY) / 16);
                const hasInfluence = this.influenceMap.get(newX, newY) === "player";
                const tile = this.currentWorld.getTile(newX, newY);
                if (hasInfluence
                    && this.canPayUnitCost("player", unit)
                    && tile
                    && unit.canEnter({ game: this, tile, unit, world: this.currentWorld, x: newX, y: newY, faction: "player" })
                ) {
                    const newObj = unit.make({ faction: "player" });
                    const moved = this.tryMove(newObj, newX, newY, this.currentWorld, true);
                    if (moved) {
                        globals.audio.playSound("move");
                        deployed = true;
                        newObj.setNotReady();
                        this.payUnitCost("player", unit)
                        this.stats.recordUnitCreated(unit.id);
                    } else {
                    }
                }
                if (!deployed) globals.audio.playSound("nope");
                this.dragObj = null;
            }

        }

    }

    // #endregion





    // #region tutorial

    tutorialSteps: ((g: Graphics) => {
        isOverworld?: boolean,
        zoom?: number,
        camX?: number, camY?: number,
        x: number, y: number, w: number, h: number,
        text: string,
        imgs: {
            img: string,
            x: number,
            y: number
        }[],
        action?: (g: Graphics) => void
        render?: (g: Graphics) => void
    })[] = [

            // STEP 0
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 120, camY: 140,
                x: 6, y: 30, w: 220, h: 36,
                text: "welcome back to the   NETHERWORLD   my count!\n" +
                    "while you were away your dear father the   KING   has\n" +
                    "perished and now all   HELL   broke loose!",
                imgs: [],
            }),

            // STEP 1
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 120, camY: 140,
                x: 6, y: 40, w: 220, h: 36,
                text: "to regain control... so we'll need a lot of BLOOD\n" +
                    "our SEAT              already produces some blood.\n",
                imgs: [
                    { img: "arrow_up", x: 84, y: 19 },
                    { img: "arrow_right", x: 120, y: 115 },
                    { img: "blood", x: 188, y: 38 },
                    { img: "seat_player", x: 49, y: 51 },
                ],
            }),

            // STEP 2
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 200, camY: 80,
                x: 6, y: 30, w: 220, h: 36,
                text: "but to get more we will need many CAULDRONs\n",
                imgs: [
                    { img: "arrow_right", x: 135, y: 110 },
                    { img: "cauldron", x: 187, y: 31 },
                ],
            }),

            // STEP 3
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 140, camY: 120,
                x: 6, y: 30, w: 220, h: 36,
                text: "now place a FIEND             unit on the battlefield.\n" +
                    "fiends are cheap and fast, but have little use otherwise.\n" +
                    "",
                imgs: [
                    { img: "fiend_player", x: 80, y: 30 },
                    { img: "arrow_down", x: 9, y: g.height / 4 - 44 },
                    { img: "arrow_up", x: 147, y: 108 },
                ],
            }),

            // STEP 4
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 200, camY: 100,
                x: 6, y: 30, w: 220, h: 36,
                text: "as you see, moving a unit EXHAUSTS it for some time,\n" +
                    "and you will need to wait for it to COOLDOWN\n" +
                    "HOVER the unit to see it's cost and usage",
                imgs: [
                    { img: "cd", x: 184, y: 42 },
                ],
            }),

            // STEP 5
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 200, camY: 100,
                x: 6, y: 30, w: 220, h: 36,
                text: "now drag the unit closer to the cauldron.\n" +
                    "you'll notice that the unit spreads our INFLUENCE.\n" +
                    "use the unit to put the cauldron under our influence.",
                imgs: [
                    { img: "influence/player", x: 203, y: 36 },
                    { img: "player_influence_border_left", x: 203, y: 36 },
                    { img: "player_influence_border_up", x: 203, y: 36 },
                    { img: "player_influence_border_down", x: 203, y: 36 },
                    { img: "player_influence_border_right", x: 203, y: 36 },
                ],
            }),

            // STEP 6
            (g: Graphics) => ({
                zoom: 2,
                x: 6, y: 30, w: 220, h: 36,
                text: "sometimes our influence is removed \n" +
                    "by the people of the OVERWORLD.\n" +
                    "press the swap world button (or enter) now.\n",
                imgs: [
                    { img: "arrow_up", x: g.width / 4 - 32, y: 42 },
                ],
            }),

            // STEP 7
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: true,
                camX: 120, camY: 140,
                x: 6, y: 40, w: 220, h: 36,
                text: "the overworld people can be snatched for SOULS\n" +
                    "you will need many more souls to spawn units.\n" +
                    "later you also need to defend our influence.\n",
                imgs: [
                    { img: "cultists", x: 178, y: 29 },
                    { img: "soul", x: 185, y: 39 },
                    { img: "arrow_up", x: 37 + 16 + 8, y: 19 },
                ],
            }),

            // STEP 8
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: true,
                camX: 120, camY: 110,
                x: 6, y: 30, w: 160, h: 36,
                text: "place a CULTIST            on the overworld\n" +
                    "on top of a PEASANT           to snatch it.\n",
                imgs: [
                    { img: "cultist_player", x: 74, y: 28 },
                    { img: "peasant", x: 89, y: 39 },
                    { img: "arrow_down", x: 9, y: g.height / 4 - 44 },
                    { img: "arrow_right", x: 150, y: 97 },
                ],
                action: () => {
                    if (!this.overworld.get(18, 13))
                        this.overworld.set(18, 13, this.units["peasant"].make({ faction: "neutral" }))
                }
            }),

            // STEP 9
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 650, camY: 140,
                x: 6, y: 30, w: 220, h: 36,
                text: "beware of other PRETENDERS that covet the throne!\n" +
                    "inspect their units and explore their INTERACTIONS\n" +
                    "for example: their imps can defeat your fiends and imps.\n",
                imgs: [
                ],
            }),

            // STEP 10
            (g: Graphics) => ({
                zoom: 4,
                isOverworld: false,
                camX: 120, camY: 140,
                x: 6, y: 30, w: 220, h: 36,
                text: "now you have every tool to crush the OPPOSITION \n" +
                    "Defeat our enemies and rise to be...\n" +
                    "                     the new KING of the NETHEREIGN.\n",
                imgs: [
                ],
            }),
        ]

    isInTutorial() {
        return this.currentTutorialStep >= 0 && this.currentTutorialStep < this.tutorialSteps.length;
    }

    renderTutorial(g: Graphics, mxy: Vec2, stepI: number) {
        const { zoom, camX, camY, x, y, w, h, text, imgs, isOverworld, render } = this.tutorialSteps[stepI](g);

        if (camX !== undefined)
            if (Math.abs(this.camX - camX) > 0)
                this.camX = Math.floor(RenderUtil.lerp(this.camX, camX, .1));
        if (camY !== undefined)
            if (Math.abs(this.camY - camY) > 0)
                this.camY = Math.floor(RenderUtil.lerp(this.camY, camY, .1));

        if (isOverworld !== undefined)
            this.isOverworld = isOverworld;

        if (zoom !== undefined)
            this.zoom = zoom;

        g.renderText("", 0, 0, { color: Color.col(255, 255, 255, 255), size: 5 });
        RenderUtil.render9Slice(g, x, y, w, h);
        g.renderText(text, x + 5, y + 10);

        {
            const buttonBounds = {
                x: x + w - 34,
                y: y + h - 10,
                w: 132 / 4,
                h: 64 / 4,
            }
            const isHovered = Rect.isIn(mxy, buttonBounds);
            if (!isHovered) g.sketch.tint(220, 255)
            RenderUtil.render9Slice(g, buttonBounds.x, buttonBounds.y, buttonBounds.w, buttonBounds.h);
            g.renderText(stepI === this.tutorialSteps.length - 1 ? " end" : "next", buttonBounds.x + 8, buttonBounds.y + 10);
            g.sketch.tint(255, 255)
            if (isHovered && this.justPressed && !this.consumedPressed) {
                globals.audio.playSound("tick")
                this.consumedPressed = true;
                this.currentTutorialStep++;

                const nextStep = this.tutorialSteps[this.currentTutorialStep];
                if (nextStep) {
                    const step = nextStep(g);
                    if (step) {
                        const action = step.action;
                        if (action) action(g)
                    }
                }
            }
        }

        if (render) render(g);

        for (const { img, x, y } of imgs) {
            g.renderImage(img, x, y);
        }

    }
    // #endregion





    // #region AI

    readonly AI_ratios: Record<Faction, Record<string, number>> =
        {
            "blue": {
                fiend: 0.30,
                imp: 0.20,
                demon: 0.10,
                machine: 0.05,
                obelisk: 0.05,
                cultist: 0.20,
                pentagram: 0.05,
                "conversion ritual": 0.05,
            },
            "green": {
                fiend: 0.20,
                imp: 0.25,
                demon: 0.15,
                machine: 0.10,
                obelisk: 0.05,
                cultist: 0.15,
                pentagram: 0.05,
                "conversion ritual": 0.05,
            },
            // "yellow": {
            //     fiend: 0.20,
            //     imp: 0.30,
            //     demon: 0.15,
            //     machine: 0.05,
            //     obelisk: 0.05,
            //     cultist: 0.20,
            //     pentagram: 0.05,
            //     "conversion ritual": 0.05,
            // },
            "neutral": {},
            "player": {},
        };

    stepAIFactions() {
        const now = Date.now();
        if (now > this.nextFactionStepTime) {
            this.nextFactionStepTime = now + 1000;
            this.stepAIFaction("blue");
            this.stepAIFaction("green");
        }
    }

    pickUnitToBuild(faction: Faction, tracking: { objs: GameObj[] }): string {

        const ratios = this.AI_ratios[faction];

        const counts: Record<string, number> = {};
        for (const type of Object.keys(ratios)) {
            counts[type] = 0;
        }

        for (const obj of tracking.objs) {
            if (obj.unitId in counts) {
                counts[obj.unitId]++;
            }
        }

        const total = tracking.objs.length;

        let bestType = Object.keys(ratios)[0];
        let bestDeficit = -Infinity;

        for (const type of Object.keys(ratios)) {
            const targetShare = ratios[type];
            const currentShare = total > 0 ? counts[type] / total : 0;
            const deficit = targetShare - currentShare;

            if (deficit > bestDeficit) {
                bestDeficit = deficit;
                bestType = type;
            }
        }

        return bestType;
    }

    pickRandomUnitWeighted(faction: Faction,): string {

        const ratios = this.AI_ratios[faction];
        const entries = Object.entries(ratios);

        const totalWeight = entries.reduce((sum, [, w]) => sum + w, 0);
        let r = Math.random() * totalWeight;

        for (const [type, weight] of entries) {
            r -= weight;
            if (r <= 0) {
                return type;
            }
        }

        return entries[entries.length - 1][0];
    }

    stepAIFaction(AIfaction: Faction) {

        const tracking = this.AITracking[AIfaction];

        for (let i = tracking.objs.length - 1; i >= 0; i--) {
            const obj = tracking.objs[i];
            const inNether = this.netherworld.getById(obj.id);
            const inOverworld = this.overworld.getById(obj.id);
            if (!inNether && !inOverworld) {
                tracking.objs.splice(i, 1);
            }
        }

        const unitId = Math.random() < 0.25
            ? this.pickRandomUnitWeighted(AIfaction)
            : this.pickUnitToBuild(AIfaction, tracking);

        const unit = this.units[unitId];

        if (this.canPayUnitCost(AIfaction, unit)) {
            const newObj = unit.make({ faction: AIfaction });
            newObj.setNotReady();
            const targetWorld = unit.playerCanDeploy({ game: this, unit, world: this.overworld, faction: AIfaction })
                ? this.overworld
                : this.netherworld;
            const created = this.tryAIPlaceNewObjInInfluence(newObj, targetWorld, AIfaction);
            if (created) {
                tracking.objs.push(newObj);
                this.payUnitCost(AIfaction, unit);
            }
        }

    }

    tryAIPlaceNewObjInRange(checkRange: number, range: number, maxNr: number, type: string, ctx: ObjContext): boolean {

        const { x, y, world, faction } = ctx;

        if (world.countObjectsInRadius(x, y, type, checkRange) >= maxNr) return false;
        if (ctx.world.getNumberOf(type) >= this.neutralUnitMaxAmount) return false;

        const unit = this.units[type];
        const newPlace = this.findRandomPosForObj(range, ctx, ctx => unit.canEnter(ctx))
        if (!newPlace) return false;

        return this.tryMove(unit.make({ faction: faction }), newPlace.x, newPlace.y, world, true)

    }

    tryAIPlaceNewObjInInfluence(obj: GameObj, world: World, faction: Faction): boolean {

        const unit = this.units[obj.unitId];

        const possiblePositions = this.influenceMap.getAll(faction);
        if (possiblePositions.length < 1) return false;

        const filteredPossiblePositions = possiblePositions.filter(({ x, y }) => {
            const tile = world.getTile(x, y);
            if (!tile) return false;
            return unit.canEnter({ game: this, x, y, tile, faction: faction, unit, world })
        });
        if (filteredPossiblePositions.length < 1) return false;

        const newPlace = filteredPossiblePositions[Math.floor(Math.random() * filteredPossiblePositions.length)]
        return this.tryMove(obj, newPlace.x, newPlace.y, world, true)
    }

    // #endregion





    // #region UI Rendering

    getAvailableUnitsToDeploy() {
        return Object.keys(this.units).filter(x => this.units[x].playerCanDeploy({ game: this, unit: this.units[x], world: this.currentWorld, faction: "player" }))
    }

    renderTopUI(g: Graphics, mxy: Vec2) {
        let x = 6;
        const y = 6;

        {
            const bounds = { x, y, w: 16, h: 16 }
            RenderUtil.render9Slice(g, x, y, 16, 16);
            g.renderImage("back", x + .5, y + .5);
            if (Rect.isIn(mxy, bounds) && !this.consumedPressed && this.justPressed) {
                this.consumedPressed = true;
                this.save();
                setCurrentScreen(new MenuScreen())
            }
        }

        x += 21;
        let dx = 4;
        RenderUtil.render9Slice(g, x, y, 152, 16);

        g.renderText("", 0, 0, { size: 7 });

        {
            dx += 32;
            const textSouls = "" + this.playerData.souls;
            const textSoulsW = g.textWidth(textSouls);
            g.renderText(textSouls, x + dx - textSoulsW + 1, y + 12.5, { color: Color.col(0, 0, 0) });
            g.renderText(textSouls, x + dx - textSoulsW, y + 11.5, { color: Color.col(209, 249, 247) });
            g.renderImage("soul", x + dx, y - 1);
        }
        {
            dx += 48;
            const textBlood = "" + this.playerData.blood;
            const textBloodW = g.textWidth(textBlood);
            g.renderText(textBlood, x + dx - textBloodW + 1, y + 12.5, { color: Color.col(0, 0, 0) });
            g.renderText(textBlood, x + dx - textBloodW, y + 11.5, { color: Color.col(255, 84, 86) });
            g.renderImage("blood", x + dx, y - 1);
        }
        {
            dx += 48;
            const textRunes = "" + this.playerData.runes;
            const textRunesW = g.textWidth(textRunes);
            g.renderText(textRunes, x + dx - textRunesW + 1, y + 12.5, { color: Color.col(0, 0, 0) });
            g.renderText(textRunes, x + dx - textRunesW, y + 11.5, { color: Color.col(180, 166, 185) });
            g.renderImage("runes", x + dx, y - 1);
        }
    }

    prevHoveredBotUI: UnitType | GameObj | null = null

    renderBotUI(g: Graphics, mxy: Vec2) {

        if (this.dragObj != null) return;

        const w = 24 + this.getAvailableUnitsToDeploy().length * 16;

        const x = 6;
        const y = g.height / 4 - 22 - 6;
        let dx = 4;
        RenderUtil.render9Slice(g, x, y, w, 24);

        g.renderText("", 0, 0, { size: 4 });

        for (const unitId of Object.keys(this.units)) {
            const unit = this.units[unitId];
            const bounds: Rect = { x: x + dx, y: y + 4, w: 16, h: 16 };
            const ctx: UnitContext = { game: this, unit, world: this.currentWorld, faction: "player" };
            if (!unit.playerCanDeploy(ctx)) continue;
            const hasResources = this.canPayUnitCost("player", unit);
            if (!hasResources) {
                g.renderImage(unit.img(ctx) + "_gray", bounds.x, bounds.y);
            } else {
                g.renderImage(unit.img(ctx), bounds.x, bounds.y);
            }
            g.sketch.tint(255, 255);
            if (Rect.isIn(mxy, bounds)) {
                g.renderImage("select", bounds.x - 1, bounds.y - 1);
                this.hovered = unit;

                if (this.justPressed && !this.consumedPressed && hasResources) {
                    globals.audio.playSound("tick");
                    this.consumedPressed = true;
                    this.dragObj = unit;
                }
            }
            dx += 20;
        }

        if (this.hovered && this.prevHoveredBotUI !== this.hovered) {
            globals.audio.playSound("tock")
        }
        this.prevHoveredBotUI = this.hovered;
    }

    renderSwapButton(g: Graphics, mxy: Vec2) {
        const swapBtnX = g.width / 4 - 49;
        const swapBtnY = 4;
        const swapBtnW = 44;
        const swapBtnH = 44;

        const swapBtnBounds: Rect = { x: swapBtnX, y: swapBtnY, w: swapBtnW, h: swapBtnH };
        const isHovering = Rect.isIn(mxy, swapBtnBounds);

        g.renderImage(this.isOverworld ? "swap_overworld" : "swap_netherworld", swapBtnX, swapBtnY);
        if (this.justPressed && !this.consumedPressed && isHovering) {
            this.consumedPressed = true;
            this.swapWorld();
        }
    }

    renderHoveredUnitUI(g: Graphics, hovered: UnitType | GameObj) {
        let title, description;
        let unit: UnitType;
        let ctx: UnitContext;
        if (hovered instanceof GameObj) {
            unit = this.units[hovered.unitId];
            ctx = { game: this, unit, world: this.currentWorld, faction: hovered.faction }
            title = unit.name(ctx)
            description = unit.description(ctx);

        } else {
            unit = hovered;
            ctx = { game: this, unit: hovered, world: this.currentWorld, faction: "player" }
            title = hovered.name(ctx);
            description = hovered.description(ctx);
        }
        const w = 168;
        const h = 48;
        const x = g.width / 4 - w - 4;
        const y = g.height / 4 - h - 4;

        RenderUtil.render9Slice(g, x - 19, y + 4, 32, 24);
        g.renderImage(unit.img(ctx), x - 16, y + 8)

        RenderUtil.render9Slice(g, x, y, w, h);
        g.renderText(description.toLowerCase(), x + 8, y + 22, { size: 4, color: Color.col(255, 255, 255, 255) });

        g.renderText(title.toUpperCase(), x + 8, y + 14, { size: 6 });


        if (unit.maxCooldown() > 0) {
            g.renderImage("cd", x + 8, y + 34)
            const cooldownText = unit.maxCooldown() / 1000 + "";
            const cooldownTextW = g.textWidth(cooldownText);
            g.renderText(cooldownText, x + 18, y + 41, { size: 6 });
            g.renderText("s", x + 18 + cooldownTextW, y + 41, { size: 4 });
        }

        g.renderText("", 0, 0, { size: 6 })
        const { souls, blood, runes } = unit.cost();
        if (souls !== 0 || blood !== 0 || runes !== 0) {
            const dx = 150;
            let dy = 4;
            {
                const text = "" + souls;
                const tw = g.textWidth(text) + 1
                g.renderText(text, x + dx - tw, y + dy + 10)
                g.renderImage("soul", x + dx, y + dy - 2)
            }
            dy += 13;
            {
                const text = "" + blood;
                const tw = g.textWidth(text) + 1
                g.renderText(text, x + dx - tw, y + dy + 10)
                g.renderImage("blood", x + dx, y + dy - 2)
            }
            dy += 13;
            {
                const text = "" + runes;
                const tw = g.textWidth(text) + 1
                g.renderText(text, x + dx - tw, y + dy + 10)
                g.renderImage("runes", x + dx, y + dy - 2)
            }
        }

    }



    // #endregion





    // #region Render Utils

    private render(g: Graphics, img: string, x: number, y: number): void {
        g.renderImage(img, -this.camX + x * 16, -this.camY + y * 16);
    }

    private centerCameraOn(g: Graphics, tileX: number, tileY: number): void {
        const tilePixelCenterX = tileX * 16 + 8;
        const tilePixelCenterY = tileY * 16 + 8;

        this.camX = Math.floor(tilePixelCenterX - (g.width / this.zoom) / 2);
        this.camY = Math.floor(tilePixelCenterY - (g.height / this.zoom) / 2);
    }

    private renderInfluenceBorders(g: Graphics, x: number, y: number, influence: string): void {
        const sides: { dx: number, dy: number, suffix: string }[] = [
            { dx: 0, dy: -1, suffix: "up" },
            { dx: 1, dy: 0, suffix: "right" },
            { dx: 0, dy: 1, suffix: "down" },
            { dx: -1, dy: 0, suffix: "left" },
        ];

        for (const { dx, dy, suffix } of sides) {
            const neighborInfluence = this.influenceMap.get(x + dx, y + dy);
            if (neighborInfluence !== influence) {
                this.render(g, influence + "_influence_border_" + suffix, x, y);
            }
        }
    }

    // #endregion





    // #region Keyboard

    checkKeyboard(g: Graphics) {

        const speed = 4;
        if (Keys.justPressed(Keys.KEY_ENTER)) this.swapWorld();

        const units = this.getAvailableUnitsToDeploy();
        if (Keys.justPressed(Keys.KEY_1)) if (units.length >= 0 && this.canPayUnitCost("player", this.units[units[0]])) this.dragObj = this.units[units[0]];
        if (Keys.justPressed(Keys.KEY_2)) if (units.length >= 1 && this.canPayUnitCost("player", this.units[units[1]])) this.dragObj = this.units[units[1]];
        if (Keys.justPressed(Keys.KEY_3)) if (units.length >= 2 && this.canPayUnitCost("player", this.units[units[2]])) this.dragObj = this.units[units[2]];
        if (Keys.justPressed(Keys.KEY_4)) if (units.length >= 3 && this.canPayUnitCost("player", this.units[units[3]])) this.dragObj = this.units[units[3]];
        if (Keys.justPressed(Keys.KEY_5)) if (units.length >= 4 && this.canPayUnitCost("player", this.units[units[4]])) this.dragObj = this.units[units[4]];

        if (Keys.isDown(Keys.KEY_LEFT) || Keys.isDown(Keys.KEY_A)) this.camX -= speed;
        if (Keys.isDown(Keys.KEY_RIGHT) || Keys.isDown(Keys.KEY_D)) this.camX += speed;
        if (Keys.isDown(Keys.KEY_UP) || Keys.isDown(Keys.KEY_W)) this.camY -= speed;
        if (Keys.isDown(Keys.KEY_DOWN) || Keys.isDown(Keys.KEY_S)) this.camY += speed;

        if (Keys.justPressed(Keys.KEY_Q) || Keys.justPressed(Keys.KEY_E)) {

            globals.audio.playSound("plim");

            const centerX = this.camX + (g.width / this.zoom) / 2;
            const centerY = this.camY + (g.height / this.zoom) / 2;

            if (Keys.justPressed(Keys.KEY_Q) && this.zoom > 1)
                this.zoom = this.cycleZoomDown();
            if (Keys.justPressed(Keys.KEY_E) && this.zoom < 8)
                this.zoom = this.cycleZoomUp();

            this.camX = Math.floor(centerX - (g.width / this.zoom) / 2);
            this.camY = Math.floor(centerY - (g.height / this.zoom) / 2 + 0.5);
        }
    }

    cycleZoomDown(): number {
        if (this.zoom === 2) return 1
        if (this.zoom === 4) return 2
        if (this.zoom === 8) return 4
        return this.zoom;
    }

    cycleZoomUp(): number {
        if (this.zoom === 1) return 2
        if (this.zoom === 2) return 4
        if (this.zoom === 4) return 8
        return this.zoom;
    }

    // #endregion






    // #region Mouse

    onMouseWheel(down: boolean) {
        //disabled because zoom doesnt work well yet
        return;
        if (down) this.zoom = this.cycleZoomDown()
        else this.zoom = this.cycleZoomUp()
    }

    isPressed(): boolean {
        return this.wasPressed;
    }

    setHovered(obj: GameObj): void {
        this.hovered = obj;
    }

    isDragObj(obj: GameObj): boolean {
        return this.dragObj === obj;
    }

    tryBeginObjDrag(ctx: RenderContext): void {
        const { obj, unit } = ctx;
        if (this.justPressed && !this.consumedPressed && unit.playerCanControl(ctx) && unit.canMove() && obj.isReady(unit.maxCooldown())) {
            this.dragObj = obj;
            this.consumedPressed = true;
        }
    }

    private updateMouse(g: Graphics): void {
        this.justPressed = g.mouseIsPressed && !this.wasPressed;
        if (this.justReleased)
            this.consumedPressed = false;
        this.justReleased = !g.mouseIsPressed && this.wasPressed;
        this.wasPressed = g.mouseIsPressed;
    }

    private updateDrag(g: Graphics): void {

        if (!this.consumedPressed && g.mouseIsPressed) {
            if (!this.dragging) {
                this.dragging = true;
                this.lastMouseX = g.mx;
                this.lastMouseY = g.my;
            } else {
                const dx = g.mx - this.lastMouseX;
                const dy = g.my - this.lastMouseY;
                this.camX -= dx / this.zoom;
                this.camY -= dy / this.zoom;
                this.camX = Math.floor(this.camX)
                this.camY = Math.floor(this.camY)

                this.lastMouseX = g.mx;
                this.lastMouseY = g.my;
            }
        } else {
            this.dragging = false;
        }
    }

    // #endregion




}
